import {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useState
} from 'react';
import { debounce } from 'lodash';

export const useLocalStorage = <T>(key: string, defaultValue: T): [T, Dispatch<SetStateAction<T>>] => {
	const [value, setValue] = useState<T>(() => {
		const val = window.localStorage.getItem(key);
		return val !== null
			? JSON.parse(val) as T
			: defaultValue;
	});

	// Limit invocations of stringify and setItem for performance
	const setItem = useCallback(
		debounce((next: T) => {
			/* Guard against the trailing edge firing after teardown (e.g. jsdom in tests), where
			   `window` is gone — this surfaced as an unhandled "window is not defined" in the suite. */
			if (typeof window === 'undefined') {
				return;
			}
			window.localStorage.setItem(key, JSON.stringify(next));
		}, 200),
		[key],
	);

	// Cancel any pending debounced write on unmount so it can't fire after the component is gone.
	useEffect(() => () => setItem.cancel(), [setItem]);

	useEffect(() => {
		setItem(value);
	}, [key, value]);

	// Flush any pending debounced write on unmount so the timer never fires without a window
	useEffect(() => {
		return () => {
			setItem.flush?.();
		};
	}, []);

	return [value, setValue];
}
